import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import acaLogo       from '../../assets/images/acalogo.png';
import acaTranspLogo from '../../assets/images/acalogo.png';
import { getLegalDocument, type LegalDocument } from '../../services/legalDocumentService';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import { useRateLimitGuard } from '../../hooks/useRateLimitGuard';
import SessionTimeoutWarning from '../SessionTimeoutWarning/SessionTimeoutWarning';
import './Layout.css';

type ModalType = 'terms' | 'privacy' | null;

/**
 * Layout Component with Integrated Modal Footer
 *
 * Provides the main page structure with:
 * - Fixed header with logo and title
 * - Main content area
 * - Footer with Terms and Privacy Policy modal links that fetch from SQL database via API
 *
 * Legal Document Fetching:
 * - Terms and Conditions: typeId = 1
 * - Privacy Policy: typeId = 2
 * - Documents are fetched from backend API: /api/legal/document/{typeId}
 */
export default function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { showWarning, secondsLeft, stayActive } = useSessionTimeout();
  useRateLimitGuard();
  const showWatermark = pathname.startsWith('/error') || pathname.toLowerCase().startsWith('/access');

  // State to track which modal is open (Terms, Privacy Policy, or none)
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  // State to store fetched legal document content from SQL database
  const [legalContent, setLegalContent] = useState<LegalDocument | null>(null);

  // Loading state - true while fetching from API
  const [isLoading, setIsLoading] = useState(false);

  // Error state - stores error message if API call fails
  const [error, setError] = useState<string | null>(null);

  // Configuration for modal - maps modal type to legal document type ID
  // Type ID 3 = Terms and Conditions
  // Type ID 2 = Privacy Policy
  const modalConfig = {
    terms: {
      title: 'Terms and Conditions',
      typeId: 4,
    },
    privacy: {
      title: 'Privacy Policy',
      typeId: 5,
    },
  };

  // Fetch legal document from SQL database when modal type changes
  useEffect(() => {
    if (!activeModal) {
      // Clear content when modal is closed
      setLegalContent(null);
      setError(null);
      return;
    }

    // Fetch the legal document from backend API (which queries SQL database)
    const fetchLegalDocument = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const config = modalConfig[activeModal];
        // Call API service which fetches from backend
        // Backend BLL: new BLL.LegalDocumentBLL().GetLegalDocumentByLegalTypeID(typeId)
        const document = await getLegalDocument(config.typeId);
        setLegalContent(document);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load document';
        setError(errorMessage);
        /* console.error(`Failed to fetch ${activeModal} document:`, err); */
      } finally {
        setIsLoading(false);
      }
    };

    fetchLegalDocument();
  }, [activeModal]);

  // Close modal and clear state
  const closeModal = () => {
    setActiveModal(null);
    setLegalContent(null);
    setError(null);
  };

  // Handle keyboard close (ESC key)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  };

  // Get current modal configuration
  const currentModal = activeModal ? modalConfig[activeModal] : null;

  return (
    <div className="layout-root">

      <header className="layout-header" role="banner">
        <div className="layout-header-inner">
          <img src={acaLogo} alt="ACA Track" className="layout-logo-img" />
          <span className="layout-header-divider" aria-hidden="true" />
          <span className="layout-header-title">Employee 1095 Form Digital Access Portal</span>
        </div>
      </header>

      <main className="layout-main" role="main">
        {showWatermark && (
          <div className="layout-watermark" aria-hidden="true">
            <img src={acaTranspLogo} alt="" className="layout-watermark-img" />
          </div>
        )}
        <div className="layout-content">
          {children}
        </div>
      </main>

      {/* ── Footer with Modal Links ────────────────────────────────────────────── */}
      <footer className="footer" role="contentinfo">
        <div className="footer-inner">
            {/* Links Section - Fetches legal documents from SQL database when clicked */}
          <div className="footer-links">
            <button
              className="footer-link"
              onClick={() => setActiveModal('terms')}
              type="button"
              aria-label="Open Terms and Conditions"
              disabled={isLoading}
            >
              Terms and Conditions
            </button>
            <span className="footer-sep" aria-hidden="true">|</span>
            <button
              className="footer-link"
              onClick={() => setActiveModal('privacy')}
              type="button"
              aria-label="Open Privacy Policy"
              disabled={isLoading}
            >
              Privacy Policy
            </button>
          </div>
           <span className="footer-sep" aria-hidden="true">|</span>
          {/* Company Info Section */}
          <div className="footer-info">
            <span className="footer-copyright">©2026 ACA Track. All rights reserved.</span>
            <span className="footer-sep" aria-hidden="true">|</span>
            <span className="footer-date">Last Updated May 22, 2026.</span>
          </div>

        </div>
      </footer>

      {/* ── Session Timeout Warning ───────────────────────────────────────────── */}
      {showWarning && (
        <SessionTimeoutWarning secondsLeft={secondsLeft} onStayActive={stayActive} />
      )}

      {/* ── Modal Dialog for Terms/Privacy Policy ─────────────────────────────── */}
      {/* Modal renders only when activeModal is set (terms or privacy) */}
      {activeModal && (
        <div
          className="modal-backdrop"
          onClick={closeModal}
          role="presentation"
        >
          {/* Modal content container - stops backdrop click from propagating */}
          <div
            className="modal-content footer-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            {/* Modal header with title and close button */}
            <div className="modal-header">
              <h2 id="modal-title" className="modal-title">
                {currentModal?.title}
              </h2>
              {/* Close button - closes modal on click */}
              <button
                className="modal-close-btn"
                onClick={closeModal}
                aria-label="Close modal"
                type="button"
              >
                ×
              </button>
            </div>

            {/* Modal body - displays fetched HTML content from SQL or loading/error state */}
            <div className="modal-body">
              {isLoading && (
                <div className="modal-loading">
                  <div className="modal-spinner"></div>
                  <p>Loading document...</p>
                </div>
              )}

              {error && (
                <div className="modal-error">
                  <p>
                    <strong>Error:</strong> {error}
                  </p>
                  <p>Unable to load the document. Please try again later.</p>
                </div>
              )}

              {legalContent && !isLoading && !error && (
                <iframe
                  srcDoc={legalContent.fileContent}
                  className="modal-iframe"
                  title={currentModal?.title}
                  sandbox="allow-same-origin"
                />
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
